import type { ReactElement } from 'react';
import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

import useAsyncEffect from 'hooks/useAsyncEffect';
import useCurrentUser from 'hooks/useCurrentUser';
import useAddresses from 'hooks/useAddresses';
import useAddCreditCard from 'hooks/useAddCreditCard';
import useSubscription from 'hooks/useSubscription';

import { post, patch } from 'utils/network';
import { runsWebview, supportsApplePay } from 'utils/device';
import { Route, ApiRoute, FeatureFlag, PaymentUpdateFunnel, PaymentService } from 'config';
import Braintree from 'services/Braintree';
import HeapAnalytics from 'services/HeapAnalytics';
import Impact from 'services/Impact';
import GTM from 'services/GTM';

import Plan from 'models/Plan';
import FetchState from 'models/FetchState';
import type { AddressErrors } from 'models/Address';

import Accordion from 'components/shared/Accordion';
import AddressView from 'components/shared/AddressView';
import PaymentMethod from 'components/shared/PaymentMethod';
import PaymentView, { PaymentProvider, UI } from 'components/shared/PaymentView';
import SubscriptionCheckbox from 'components/shared/PaymentView/SubscriptionCheckbox';
import PaymentInfoSkeleton from 'components/shared/PaymentView/PaymentInfoSkeleton';
import PayPalProvider from 'components/shared/PaymentView/PayPalProvider';
import GooglePayProvider from 'components/shared/PaymentView/GooglePayProvider';
import ApplePayProvider from 'components/shared/PaymentView/ApplePayProvider';
import Checkbox from 'components/shared/Checkbox';
import BillingAddressView from 'components/shared/BillingAddressView';
import SkeletonLine from 'components/shared/skeletons/SkeletonLine';
import PandoraAdXcelPixel from 'components/shared/PandoraAdXcelPixel';
import { Component as LoadingIcon } from 'icons/loading.svg';
import ProgressBar from '../../shared/ProgressBar';

const urlStepsRegExp = /address|payment/;

// TODO: this file is way too long - components should be broken out
function Steps(): ReactElement {
  const navigate = useNavigate();
  const shippingStepRef = useRef(null);
  const paymentStepRef = useRef(null);
  const addCreditCard = useAddCreditCard();
  const { subscription, fetchState: subscriptionFetchState } = useSubscription();
  const { signup_summary: signupSummary } = subscription;

  const {
    currentUser,
    reload: reloadUser,
    fetchState: userFetchState,
  } = useCurrentUser();

  const {
    payment_method: paymentMethod,
    billing_address: billingAddress,
  } = currentUser;

  const {
    addresses,
    reload: reloadAddresses,
    fetchState: addressesFetchState,
  } = useAddresses();

  const plan = new Plan(currentUser.subscription?.plan);

  const hasGiveaway = signupSummary
    ? signupSummary.discount?.type === 'giveaway_discount'
    : true;

  const hasGift = signupSummary
    ? signupSummary.discount?.type === 'gift_discount'
    : true;

  const hasTotalValueCovered = !signupSummary?.box_total;
  const oauthInfo = signupSummary?.oauth_info;

  const [isAddressActive, setAddressActive] = useState(false);
  const [isAddressCompleted, setAddressCompleted] = useState(false);
  const [isPaymentActive, setPaymentActive] = useState(false);
  const [isPaymentCompleted, setPaymentCompleted] = useState(false);

  const [isBillingAddressActive, setIsBillingAddressActive] = useState(false);
  const [isBillingEqShipping, setIsBillingEqShipping] = useState(true);

  const shouldRenderChangeBillingAddressButton = !isBillingEqShipping
    && !isBillingAddressActive;
  const hasSkipSurveyFF = currentUser.hasFeatureEnabled(
    FeatureFlag.SKIP_WELCOME_SURVEY,
  );

  const [ccNumber, setCCNumber] = useState('');
  const [expMonth, setExpMonth] = useState('');
  const [expYear, setExpYear] = useState('');
  const [cvc, setCvc] = useState('');
  const [error, setError] = useState('');

  const [selectedProvider, setSelectedProvider] = useState(null);
  const [isAccountActivating, setAccountActivating] = useState(false);
  const [isPaymentProcessing, setPaymentProcessing] = useState(false);
  const [subscriptionConfirmed, setSubscriptionConfirmed] = useState(false);

  const [isBraintreeReady, setIsBraintreeReady] = useState(false);
  const [isExpCheckoutProcessing, setExpCheckoutProcessing] = useState(false);
  const [isExpCheckoutCompleted, setExpCheckoutCompleted] = useState(false);
  const [prefillAddressInfo, setPrefillAddressInfo] = useState(null);
  const [prefillAddressErrors, setPrefillAddressErrors] = useState({} as AddressErrors);

  const isGooglePayEnabled = currentUser.hasFeatureEnabled(FeatureFlag.GOOGLE_PAY);
  const isApplePayEnabled = currentUser.hasFeatureEnabled(FeatureFlag.APPLE_PAY);

  const isAppleExpressCheckoutEnabled = currentUser.hasFeatureEnabled(
    FeatureFlag.APPLE_EXPRESS_CHECKOUT,
  );

  const isUserLoaded = userFetchState === FetchState.SUCCESS
    || userFetchState === FetchState.RELOADING;

  const areAddressesLoaded = addressesFetchState === FetchState.SUCCESS
    || addressesFetchState === FetchState.RELOADING;

  const isSubscriptionLoaded = subscriptionFetchState === FetchState.SUCCESS
    || subscriptionFetchState === FetchState.RELOADING;

  const isProgramsEnabled = currentUser.hasFeatureEnabled(FeatureFlag.PROGRAMS);
  // TODO: PP-11624: use the label property from the MealTypes.programs
  const isProgramsUser = subscription.plan === 'jumpstart_program';
  const isHigherConversionOnboardingEnabled = currentUser.hasFeatureEnabled(
    FeatureFlag.HIGHER_CONVERSION_ONBOARDING,
  );
  const isDiversifiedPlansEnabled = currentUser.hasFeatureEnabled(
    FeatureFlag.DIVERSIFIED_PLANS,
  );
  const showAccountSection = (isProgramsEnabled && isProgramsUser)
    || isHigherConversionOnboardingEnabled || isDiversifiedPlansEnabled;

  useEffect(() => {
    setAddressActive(!addresses.length);
    setAddressCompleted(!!addresses.length);

    setPaymentActive(
      ((!!addresses.length && !paymentMethod) || selectedProvider !== null)
      || (isAccountActivating && selectedProvider === PaymentProvider.CREDIT_CARD),
    );

    setPaymentCompleted(!!paymentMethod);
    setIsBillingEqShipping(addresses[0]?.equals(billingAddress));
  }, [
    addresses,
    paymentMethod,
    billingAddress,
    isAccountActivating,
    selectedProvider,
  ]);

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const hasSearchParams = !!searchParams.toString();
    const newPathname = addresses.length
      ? window.location.pathname.replace(urlStepsRegExp, 'payment')
      : window.location.pathname.replace(urlStepsRegExp, 'address');

    const newURL = `${newPathname}${hasSearchParams ? `?${searchParams}` : ''}`;
    window.history.replaceState(null, '', newURL);
  }, [addresses]);

  useEffect(() => {
    if (hasGift) {
      setAddressActive(true);
      setPaymentActive(false);
    }
  }, [hasGift]);

  useAsyncEffect(async (isMounted) => {
    await Braintree.initialize();
    if (isMounted.current) setIsBraintreeReady(true);
  }, []);

  const submitAddress = async (address) => {
    const payload = {
      shipping_address: address,
      same_as_billing: isBillingEqShipping,
    };

    return post(ApiRoute.SHIPPING_ADDRESSES, payload);
  };

  const addPaymentMethod = async (token, providerName) => {
    await post(ApiRoute.PAYMENT_METHOD, {
      user: {
        payment_method_attributes: {
          token,
          provider_name: providerName,
        },
      },
    });
  };

  const submitAddressAndPaymentToken = async (providerName, token, address) => {
    setExpCheckoutProcessing(true);
    setAddressActive(false);

    HeapAnalytics.trackSuccessfullyAuthPayment(
      PaymentUpdateFunnel.EXPRESS_SIGNUP,
      providerName,
    );

    const response = await submitAddress(address);

    if (response.status === 422) {
      setPrefillAddressInfo(address);
      setPrefillAddressErrors(response.parsedBody as AddressErrors);
      setAddressActive(true);
    } else {
      reloadAddresses();
    }

    await addPaymentMethod(token, providerName);
    reloadUser();

    setExpCheckoutProcessing(false);
    setExpCheckoutCompleted(true);
  };

  const activateSubscription = async () => {
    setAccountActivating(true);

    await patch(ApiRoute.ACTIVATE, {
      subscription_agreement: 'true',
    });

    if (hasGift && hasTotalValueCovered) {
      reloadAddresses();
    }

    reloadUser();
    Impact.trackSignup(currentUser, subscription);
    GTM.trackSignup(currentUser, subscription);

    if (runsWebview) {
      // Mobile success pages rely on DOMContentLoaded
      // event so we need to trigger the page reload.
      window.location.href = Route.PROFILE_MOBILE;
    } else {
      if (!hasSkipSurveyFF) {
        localStorage.setItem('showWelcomeSurvey', new Date().toUTCString());
      }

      // this is to support the tanstack setup in the new platform
      navigate(`${Route.UPCOMING_MENUS}#welcome`, {
        state: {
          invalidate: true,
          invalidateMenus: true,
        },
      });
    }
  };

  const resetBillingAddress = async () => {
    const payload = {
      billing_address: {
        address: addresses[0]?.address,
        address2: addresses[0]?.address2,
        city: addresses[0]?.city,
        state: addresses[0]?.state,
        zip: addresses[0]?.zip,
      },
    };

    await post(`${ApiRoute.USERS}/${currentUser.id}/billing_address`, payload);

    setIsBillingAddressActive(false);
    reloadUser();
  };

  const renderBillingAddressSummary = () => (
    <>
      {`${billingAddress.firstLine} ${billingAddress.secondLine || ''}`}
      <br />
      {billingAddress.lastLine}
    </>
  );

  const renderBillingAddressForm = () => (
    <div className="c-billing-address-form">
      <div className="c-billing-address-form__content">
        <h2
          className={[
            'c-heading',
            'c-heading--brand-8',
            'c-billing-address-form__header',
          ].join(' ')}
        >
          Billing Address
          {shouldRenderChangeBillingAddressButton && (
            <span className="u-text--md">
              <button
                type="button"
                className="c-link"
                onClick={() => {
                  setIsBillingAddressActive(true);
                  setAddressActive(false);
                }}
              >
                Change
              </button>
            </span>
          )}
        </h2>
        <Checkbox
          id="billing-address-eq-shipping"
          label="Same as Shipping Address"
          checked={isBillingEqShipping}
          onChange={(e) => {
            if (e.target.checked) {
              resetBillingAddress();
            }
            setIsBillingEqShipping(e.target.checked);
            setIsBillingAddressActive(true);
          }}
        />
        {(isBillingAddressActive && !isBillingEqShipping) ? (
          <BillingAddressView
            address={billingAddress}
            userID={currentUser?.id}
            submitButtonLabel="Save and Continue"
            onSuccess={() => {
              setIsBillingAddressActive(false);
              reloadUser();
            }}
          />
        ) : (
          renderBillingAddressSummary()
        )}
      </div>
    </div>
  );

  return (
    <>
      {isDiversifiedPlansEnabled && (
        <div className="c-signup-order-summary__progress-bar">
          <ProgressBar currentStepNum={isPaymentActive || isPaymentCompleted ? 6 : 5} />
        </div>
      )}
      {/* keeping Account section out of accordion - behaves differently but we want the styling */}
      {/* TODO: clean up Accordion component to allow for this */}
      {showAccountSection && (
        <div className="c-accordion-step no-divider">
          <div className="c-accordion-step__content">
            <div className="c-accordion-step__header">
              <h2 className="c-heading c-heading--brand-7">
                Account
                <span className="u-text--xs">{currentUser.email}</span>
              </h2>
            </div>
          </div>
        </div>
      )}

      <Accordion stepByStep>
        {!(isExpCheckoutCompleted
        || (hasGift && hasTotalValueCovered)
        || addresses.length) && (
          <Accordion.Step
            title={(
              <>
                Express Checkout
                <span className="u-text--xs">
                  (Or signup with a credit card below)
                </span>
              </>
            )}
            activeSection={
              isUserLoaded && areAddressesLoaded && isSubscriptionLoaded && (
                <div className="c-alternative-payment-buttons">
                  {isExpCheckoutProcessing && (
                    <div>Processing...</div>)}
                  {!isBraintreeReady && (
                    <>
                      <SkeletonLine width={250} height={35} />

                      {isGooglePayEnabled && (
                        <SkeletonLine width={250} height={35} />
                      )}

                      {isApplePayEnabled && supportsApplePay
                      && isAppleExpressCheckoutEnabled && (
                        <SkeletonLine width={250} height={35} />
                      )}
                    </>
                  )}

                  <PayPalProvider
                    show={isBraintreeReady && !isExpCheckoutProcessing}
                    standalone
                    onToken={submitAddressAndPaymentToken}
                  />

                  {isGooglePayEnabled && (
                    <GooglePayProvider
                      show={isBraintreeReady && !isExpCheckoutProcessing}
                      standalone
                      onToken={submitAddressAndPaymentToken}
                    />
                  )}

                  {supportsApplePay && isApplePayEnabled
                  && isAppleExpressCheckoutEnabled && (
                    <ApplePayProvider
                      show={isBraintreeReady && !isExpCheckoutProcessing}
                      standalone
                      onToken={submitAddressAndPaymentToken}
                    />
                  )}
                </div>
              )
}
            completedSection=""
            isActive
            isCompleted
            setActive={() => null}
            setCompleted={() => null}
          />
        )}

        <Accordion.Step
          stepRef={shippingStepRef}
          title="Shipping Address"
          activeSection={
            areAddressesLoaded && isAddressActive && (
              <AddressView
                oauthInfo={oauthInfo}
                initialAddress={addresses[0] || prefillAddressInfo}
                initialErrors={prefillAddressErrors}
                submitButtonDisabled={isAccountActivating}
                submitButtonLabel={(
                  <>
                    {isAccountActivating && <LoadingIcon height="23" />}

                    {!isAccountActivating && (
                      hasGift && hasTotalValueCovered
                        ? 'Get My Gift'
                        : 'Save'
                    )}
                  </>
                )}
                onSuccess={() => {
                  if (hasGift && hasTotalValueCovered) {
                    activateSubscription();
                  } else {
                    reloadAddresses();
                    reloadUser();
                    setAddressCompleted(true);
                    setAddressActive(false);
                    setPaymentActive(true);
                    // @ts-expect-error TODO: fix null safety
                    shippingStepRef.current.scrollIntoView();
                  }
                }}
                updateBillingAddress={isBillingEqShipping}
              />
            )
          }
          completedSection={
            areAddressesLoaded && (
              <>
                {addresses[0]?.fullName}
                <br />
                {`${addresses[0]?.firstLine} ${addresses[0]?.secondLine}`}
                <br />
                {addresses[0]?.lastLine}
                <br />
                {`Tel: ${addresses[0]?.local_phone_number}`}
              </>
            )
          }
          isActive={isAddressActive}
          isCompleted={isAddressCompleted}
          setActive={setAddressActive}
          setCompleted={setAddressCompleted}
        />

        <Accordion.Step
          stepRef={paymentStepRef}
          title="Payment Information"
          hidden={hasGift && hasTotalValueCovered}
          hideChangeButton
          activeSection={(
            <>
              {renderBillingAddressForm()}

              {!isPaymentProcessing && (
                <h2 className="c-heading c-heading--brand-8">
                  Choose Payment: You won’t be charged today.
                  Payment info is required to activate your account.
                </h2>
              )}

              {hasGiveaway && !isPaymentProcessing && (
                <div className="row">
                  <div className="col-12">
                    <div className="c-payment-form__info">
                      <strong>
                        You will not be charged for&nbsp;
                        {plan.includedDinners}
                        &nbsp;Dinners.
                      </strong>
                      <span className="d-inline-block">
                        You can skip or cancel your subscription at anytime.
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {isPaymentProcessing ? (
                <PaymentInfoSkeleton />
              ) : (
                <PaymentView
                  ui={UI.TABS}
                  ccNumber={ccNumber}
                  expMonth={expMonth}
                  expYear={expYear}
                  cvc={cvc}
                  error={error}
                    // @ts-expect-error TODO: fix null safety
                  selectedProvider={selectedProvider}
                  setCCNumber={setCCNumber}
                  setExpMonth={setExpMonth}
                  setExpYear={setExpYear}
                  setCvc={setCvc}
                    // @ts-expect-error TODO: fix null safety
                  setSelectedProvider={setSelectedProvider}
                  onAdd={() => {
                    setPaymentProcessing(true);
                  }}
                  onSuccess={() => {
                    reloadUser();
                    setSelectedProvider(null);
                    setPaymentCompleted(true);
                    setPaymentActive(false);
                    setPaymentProcessing(false);
                  }}
                />
              )}

              {(selectedProvider === null
              || selectedProvider === PaymentProvider.CREDIT_CARD) && (
                <div id="payment-method-cc-submit" className="mt-3">
                  <SubscriptionCheckbox
                    subscriptionConfirmed={subscriptionConfirmed}
                    setSubscriptionConfirmed={setSubscriptionConfirmed}
                  />

                  <div className="c-signup-checkout__place-order">
                    <button
                      type="button"
                      className={[
                        'c-button',
                        'c-button--primary',
                        'd-block',
                        'd-md-inline-block',
                      ].join(' ')}
                      title={'To continue, you need to agree'
                        + 'with Weekly subscription above'}
                      disabled={!(
                        ccNumber && expMonth && expYear && cvc
                        && !isAccountActivating && subscriptionConfirmed
                      )}
                      onClick={() => {
                        // @ts-expect-error TODO: fix null safety
                        setSelectedProvider(PaymentProvider.CREDIT_CARD);

                        addCreditCard(
                          ccNumber,
                          cvc,
                          expMonth,
                          expYear,
                          (err) => {
                            setError(err);

                            if (err !== '') {
                              // @ts-expect-error TODO: fix null safety
                              paymentStepRef.current.scrollIntoView();
                            }
                          },
                          setAccountActivating,
                          () => {
                            HeapAnalytics.trackSuccessfullyAuthPayment(
                              PaymentUpdateFunnel.STANDARD_SIGNUP,
                              PaymentService.CREDIT_CARD,
                            );

                            activateSubscription();
                          },
                          billingAddress,
                        );
                      }}
                    >
                      {isAccountActivating
                        ? <LoadingIcon height="23" />
                        : 'Complete Subscription'}
                    </button>
                  </div>
                  <div className="d-inline-block u-text u-text--md">
                    Sign up to customize the recipes in your box each week
                  </div>
                </div>
              )}
            </>
          )}
          completedSection={(
            <>
              {renderBillingAddressForm()}

              {paymentMethod && (
                <div className="c-form-group d-flex justify-content-between">
                  <PaymentMethod
                    paymentMethod={paymentMethod}
                    iconClassName="c-signup-checkout__payment-icon"
                  />
                  <span className="u-text--md">
                    <button
                      type="button"
                      className="c-link"
                      onClick={() => {
                        setPaymentActive(true);
                        setIsBillingAddressActive(false);
                        setAddressActive(false);
                      }}
                    >
                      Change
                    </button>
                  </span>
                </div>
              )}

              <div id="payment-method-cc-submit" className="mt-3">
                <SubscriptionCheckbox
                  subscriptionConfirmed={subscriptionConfirmed}
                  setSubscriptionConfirmed={setSubscriptionConfirmed}
                />

                <div className="c-signup-checkout__place-order">
                  <button
                    type="button"
                    className={[
                      'c-button',
                      'c-button--primary',
                      'd-block',
                      'd-md-inline-block',
                    ].join(' ')}
                    title={'To continue, you need to agree'
                      + 'with Weekly subscription above'}
                    disabled={!subscriptionConfirmed || isAccountActivating}
                    onClick={activateSubscription}
                  >
                    {isAccountActivating
                      ? <LoadingIcon height="23" />
                      : 'Complete Subscription'}
                  </button>
                </div>
                <div className="d-inline-block u-text u-text--md">
                  Sign up to customize the recipes in your box each week
                </div>
              </div>
            </>
          )}
          isActive={isPaymentActive}
          isCompleted={isPaymentCompleted}
          setActive={setPaymentActive}
          setCompleted={setPaymentCompleted}
        />
      </Accordion>

      {isAccountActivating && <PandoraAdXcelPixel />}
    </>
  );
}

export default Steps;
